import React, { useState, useEffect, useRef } from 'react';
import { PersistentEmmaInterface } from '../components/emma/persistent-interface/PersistentEmmaInterface';
import { ArtifactPanel } from '../components/emma/artifacts/ArtifactPanel';
import type { ArtifactData } from '../types/emma-artifact.types';
import { GlobalSidebar } from '../components/layout/GlobalSidebar';
import { WorkspaceSwitcher } from '../components/WorkspaceSwitcher';
import { useTenant } from '../context/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { LEGAL_STRINGS } from '../constants/legalStrings';
import '../components/emma/persistent-interface/persistent-interface.css';

interface EmmaFirstLayoutProps {
  children: React.ReactNode;
}

export const EmmaFirstLayout: React.FC<EmmaFirstLayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const { activeTenant, userRole } = useTenant();
  const [layout, setLayout] = useState<'split' | 'sidebar' | 'bottom-sheet'>('split');
  const [emmaWidth, setEmmaWidth] = useState(28);
  const [isResizing, setIsResizing] = useState(false);
  const [emmaCollapsed, setEmmaCollapsed] = useState(false);
  const [activeArtifact, setActiveArtifact] = useState<ArtifactData | null>(null);
  const [isArtifactOpen, setIsArtifactOpen] = useState(false);

  // 1. Rehydrate active artifact from sessionStorage on initial mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('emma_active_artifact');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.artifact) {
          if (parsed.artifact.content && parsed.artifact.content.includes('✨ **Asset Generated')) {
            sessionStorage.removeItem('emma_active_artifact');
          } else {
            setActiveArtifact(parsed.artifact);
            if (parsed.isOpen) {
              setIsArtifactOpen(true);
              setEmmaWidth(25);
            }
          }
        }
      }
    } catch (e) {
      console.warn('Failed to rehydrate active artifact from sessionStorage:', e);
    }
  }, []);

  // 2. Persist active artifact state changes to sessionStorage
  useEffect(() => {
    try {
      if (activeArtifact) {
        sessionStorage.setItem(
          'emma_active_artifact',
          JSON.stringify({ artifact: activeArtifact, isOpen: isArtifactOpen })
        );
      }
    } catch (e) {
      console.warn('Failed to persist active artifact:', e);
    }
  }, [isArtifactOpen, activeArtifact]);

  useEffect(() => {
    const handleOpenArtifact = (e: Event) => {
      const customEvent = e as CustomEvent;
      const detail = customEvent.detail || {};
      if (detail) {
        const artObj = detail.artifact || detail.detail?.artifact || detail.detail || detail;
        let content = artObj.content || artObj.markup || artObj.visual_template || artObj.genui_html || detail.content || detail.markup || '';
        let title = artObj.title || detail.title || artObj.name || detail.name;
        let contentType = artObj.contentType || artObj.content_type || detail.contentType || detail.content_type;
        let artifactId = artObj.artifactId || artObj.artifact_id || artObj.id || detail.artifactId || detail.artifact_id;
        let slug = artObj.slug || detail.slug || artObj.uiAction?.slug || detail.uiAction?.slug;
        let previewUrl = artObj.previewUrl || artObj.preview_url || detail.previewUrl || detail.preview_url || artObj.uiAction?.preview_url || detail.uiAction?.preview_url;

        // Fallback to activeArtifact or sessionStorage if detail content is missing/empty
        if (!content && activeArtifact?.content && !activeArtifact.content.includes('✨ **Asset Generated')) {
          content = activeArtifact.content;
          title = title || activeArtifact.title;
          contentType = contentType || activeArtifact.contentType;
          artifactId = artifactId || activeArtifact.artifactId;
          slug = slug || activeArtifact.slug;
          previewUrl = previewUrl || activeArtifact.previewUrl;
        }

        if (!content) {
          try {
            const stored = sessionStorage.getItem('emma_active_artifact');
            if (stored) {
              const parsed = JSON.parse(stored);
              if (parsed?.artifact?.content && !parsed.artifact.content.includes('✨ **Asset Generated')) {
                content = parsed.artifact.content;
                title = title || parsed.artifact.title;
                contentType = contentType || parsed.artifact.contentType;
                artifactId = artifactId || parsed.artifact.artifactId;
                slug = slug || parsed.artifact.slug;
                previewUrl = previewUrl || parsed.artifact.previewUrl;
              }
            }
          } catch (err) {}
        }

        const newArt = {
          artifactId: artifactId || `art_${Date.now()}`,
          title: title || 'Generated Artifact',
          contentType: contentType || 'poster',
          content: content || '',
          slug: slug,
          previewUrl: previewUrl,
          version: detail.version || artObj.version || 1,
          uiAction: detail
        };
        setActiveArtifact(newArt);
        setIsArtifactOpen(true);
        setEmmaWidth(35);

        if (contentWrapperRef.current) {
          contentWrapperRef.current.scrollLeft = 0;
        }
      }
    };

    window.addEventListener('emma-open-artifact-panel', handleOpenArtifact);
    return () => {
      window.removeEventListener('emma-open-artifact-panel', handleOpenArtifact);
    };
  }, [activeArtifact]);

  const containerRef = useRef<HTMLDivElement>(null);
  const contentWrapperRef = useRef<HTMLDivElement>(null);

  const effectiveRole = (user?.role || userRole || '').toLowerCase();
  const isSystemAdmin = ['super_admin', 'system_admin', 'platform_admin', 'admin'].includes(effectiveRole) || Boolean(user?.is_platform_user);

  useEffect(() => {
    const updateLayout = () => {
      const width = window.innerWidth;
      if (width < 768) setLayout('bottom-sheet');
      else if (width < 1024) setLayout('sidebar');
      else setLayout('split');
    };
    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsResizing(true);
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Percent must be relative to the main+Emma row only (not full layout incl. sidebar)
      if (!isResizing || !contentWrapperRef.current) return;

      const rect = contentWrapperRef.current.getBoundingClientRect();
      const rowWidth = rect.width;
      if (rowWidth <= 0) return;
      const pointerInRow = e.clientX - rect.left;
      const newEmmaWidth = ((rowWidth - pointerInRow) / rowWidth) * 100;
      setEmmaWidth(Math.max(20, Math.min(45, newEmmaWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Story 4.1: Render layout content (split / sidebar / bottom-sheet)
  const renderLayoutContent = () => {
    if (layout === 'split') {
      return (
        <div className="flex-1 flex flex-row h-full w-full min-w-0 overflow-hidden bg-slate-900">
          {/* Main content or Artifact Panel */}
          <div className={`h-full min-w-0 overflow-hidden flex flex-col ${isArtifactOpen ? 'order-2 flex-1 bg-white dark:bg-slate-900 min-w-[480px]' : 'order-1 flex-1 bg-slate-50 dark:bg-slate-900'}`}>
            {isArtifactOpen ? (
              <ArtifactPanel
                artifact={activeArtifact}
                isOpen={isArtifactOpen}
                onClose={() => {
                  setIsArtifactOpen(false);
                  setEmmaWidth(28);
                }}
                onContentSave={(updated) => {
                  if (activeArtifact) {
                    setActiveArtifact({ ...activeArtifact, content: updated });
                  }
                }}
              />
            ) : (
              <main
                className="content-area min-h-0 emma-scroll-main flex-1 bg-slate-50 dark:bg-slate-900"
                style={{
                  flex: '1 1 0%',
                  minWidth: 0,
                  minHeight: 0,
                  alignSelf: 'stretch',
                  overflowY: 'auto',
                  overflowX: 'clip',
                }}
              >
                {children}
              </main>
            )}
          </div>

          {/* Emma collapse/expand toggle (visible when artifact panel is closed) */}
          {!isArtifactOpen && (
            <button
              onClick={() => setEmmaCollapsed(prev => !prev)}
              className="order-2 flex-shrink-0 w-6 h-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 border-l border-r border-gray-200 cursor-pointer transition-colors group"
              title={emmaCollapsed ? 'Show Emma' : 'Hide Emma'}
              style={{ zIndex: 10 }}
            >
              <span className="text-gray-400 group-hover:text-gray-600 text-xs font-bold select-none" style={{ writingMode: 'vertical-rl' }}>
                {emmaCollapsed ? '◀ Emma' : '▶'}
              </span>
            </button>
          )}

          {/* Persistent Emma Interface — kept continuously mounted to preserve WebSocket stream */}
          {(!emmaCollapsed || isArtifactOpen) && (
            <div
              className={`h-full overflow-hidden flex flex-col shrink-0 ${isArtifactOpen ? 'order-1 emma-artifact-mode-chat border-r border-slate-200 dark:border-slate-800' : 'order-3 border-l border-slate-200 dark:border-slate-800'}`}
              style={{
                width: isArtifactOpen ? '35%' : `${emmaWidth}%`,
                minWidth: isArtifactOpen ? '320px' : '280px'
              }}
            >
              {!isArtifactOpen && (
                <div
                  className="resize-handle"
                  onMouseDown={handleMouseDown}
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Resize Emma panel"
                />
              )}
              <PersistentEmmaInterface layout="split" width={100} />
            </div>
          )}
        </div>
      );
    }

    if (layout === 'sidebar') {
      return (
        <>
          <main
            className="content-area min-h-0 emma-scroll-main"
            style={{
              flex: '1 1 0%',
              minWidth: 0,
              minHeight: 0,
              alignSelf: 'stretch',
              overflowY: 'auto',
              overflowX: 'clip',
            }}
          >
            {children}
          </main>
          <PersistentEmmaInterface layout="sidebar" position="right" />
        </>
      );
    }

    // bottom-sheet (mobile)
    return (
      <>
        <main
          className="content-area min-h-0 emma-scroll-main"
          style={{
            flex: '1 1 0%',
            minWidth: 0,
            minHeight: 0,
            alignSelf: 'stretch',
            overflowY: 'auto',
            overflowX: 'clip',
          }}
        >
          {children}
        </main>
        <PersistentEmmaInterface layout="bottom-sheet" />
      </>
    );
  };

  return (
    <div className="emma-first-layout flex flex-col h-screen overflow-hidden" ref={containerRef}>
      {/* Top Navigation Header with Workspace Switcher (Super-Admin / System Admin Exclusive) */}
      <header className="h-14 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-4 z-40 shrink-0">
        <div className="flex items-center gap-3">
          {isSystemAdmin ? (
            <>
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Workspace:</span>
              <div className="w-56">
                <WorkspaceSwitcher />
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-200">
                {activeTenant ? activeTenant.name : 'Workspace'}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-emerald-400 bg-emerald-950/60 border border-emerald-800/80 px-2.5 py-1 rounded-full font-medium">
            Active Workspace Scope
          </span>
        </div>
      </header>
      <div className="flex-1 flex flex-row min-h-0 overflow-hidden">
        <GlobalSidebar />
        {/* Content wrapper — flex:1 so percentages resolve against remaining space after sidebar */}
        <div
          ref={contentWrapperRef}
          className="emma-content-row"
          style={{
            flex: '1 1 0%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            minWidth: 0,
            minHeight: 0,
            overflowX: 'hidden',
            overflowY: 'hidden',
          }}
        >
          {renderLayoutContent()}
        </div>
      </div>
      {/* Mandated Global Persistent Non-Dismissible Legal App Shell Footer */}
      <footer className="w-full bg-slate-900 border-t border-slate-800 text-slate-300 text-[11px] py-1.5 px-4 text-center select-none z-50 shrink-0 font-medium">
        {LEGAL_STRINGS.FOOTER_DISCLAIMER}
      </footer>
    </div>
  );
};
